import { days, SimpleActivity } from "@ac-expo/api-types";
import { Time } from "@internationalized/date";
import { Day, EventTimetable, EventTimetableState, Timetable } from "@prisma/client";
import {
  addDays,
  compareAsc,
  differenceInCalendarDays,
  differenceInDays,
  differenceInHours,
  differenceInMinutes,
  endOfDay,
  endOfWeek,
  format,
  FormatOptions,
  isAfter,
  isBefore,
  isSameDay,
  isSameMonth,
  isSameYear,
  isValid,
  isWithinInterval,
  parse,
  parseISO,
  set,
  setHours,
  setMinutes,
  startOfDay,
  startOfWeek,
  subDays,
} from "date-fns";
import { toZonedTime } from "date-fns-tz";
import { fr } from "date-fns/locale";
import { groupBy, isUndefined, maxBy, minBy } from "lodash";
import { TimeValue } from "react-aria";
import { DateRange } from "./date-ranges.utils";
import { isNullish } from "./utils";

export const DATABASE_DATE_FORMAT = "yyyy-MM-dd" as const;
export const TIME_FORMAT = "HH:mm" as const;
export const DATE_TIME_FORMAT = `${DATABASE_DATE_FORMAT} ${TIME_FORMAT}` as const;

type EventScheduleTimetable = Omit<
  EventTimetable,
  "id" | "eventDateRangeId" | "createdAt" | "updatedAt"
>;

export type EventScheduleType = {
  fromDate: string;
  toDate: string;
  timetables: EventScheduleTimetable[];
};

export type EventExceptionalClosingDateType = {
  date: string;
};

export type EventScheduleTypeWithClosedDates = {
  closedDates: EventExceptionalClosingDateType[];
} & EventScheduleType;

export type EventDateType = {
  schedules: EventScheduleType[];
  exceptionalClosingDates: EventExceptionalClosingDateType[];
  isContinuous: boolean;
};

export const START_HOUR = 10;
export const END_HOUR = 18;

export const FRENCH_MONTH_NAMES = [
  "janvier",
  "février",
  "mars",
  "avril",
  "mai",
  "juin",
  "juillet",
  "août",
  "septembre",
  "octobre",
  "novembre",
  "décembre",
] as const;

export const FRENCH_CROPPED_DAY_NAMES = ["dim", "lun", "mar", "mer", "jeu", "ven", "sam"] as const;

export const getFrenchMonthName = (month: number): string => {
  if (month < 0 || month > 11) {
    throw new Error("Month must be between 0 and 11");
  }
  return FRENCH_MONTH_NAMES[month];
};

export const getFrenchCroppedDayName = (day: number): string => {
  if (day < 0 || day > 6) {
    throw new Error("Day must be between 0 and 6");
  }
  return FRENCH_CROPPED_DAY_NAMES[day];
};

export function extractTimetableDates(timetable: Timetable) {
  const start = parse(`${timetable.day} ${timetable.startHour}`, "yyyy-MM-dd HH:mm", new Date());
  const end = parse(`${timetable.day} ${timetable.endHour}`, "yyyy-MM-dd HH:mm", new Date());

  return { start, end };
}

export function formatSchedules(dates: EventDateType) {
  const formatDate = (date: Date | string) => {
    if (!(date instanceof Date) || isNaN(date.getTime())) {
      return "-";
    }
    return format(date, "dd/MM/yyyy");
  };

  if (dates.schedules.length === 0) {
    return "-";
  }

  const firstNormal = dates.schedules
    .map((schedule: EventScheduleType) => new Date(schedule.fromDate))
    .filter(isValid)
    .sort((a, b) => (isBefore(a, b) ? -1 : 1))[0];
  const lastNormal = dates.schedules
    .map((schedule: EventScheduleType) => new Date(schedule.toDate))
    .filter(isValid)
    .sort((a, b) => (isAfter(a, b) ? -1 : 1))[0];

  if (firstNormal === undefined) {
    if (lastNormal === undefined) return "-";
    return formatDate(lastNormal);
  }
  if (lastNormal === undefined) return formatDate(firstNormal);

  if (isSameDay(firstNormal, lastNormal)) {
    return formatDate(firstNormal);
  }

  return `${formatDate(firstNormal)} à ${formatDate(lastNormal)}`;
}

export function getDatesBetween(from: Date | string, to: Date | string) {
  if (typeof from === "string") from = new Date(from);
  if (typeof to === "string") to = new Date(to);
  if (from > to) {
    throw new Error("'from' date must be before 'to' date");
  }

  const dates = [];
  const currentDate = startOfDay(from);

  while (currentDate <= to) {
    dates.push(new Date(currentDate));
    currentDate.setDate(currentDate.getDate() + 1);
  }

  return dates;
}

export function getRangesDates(ranges: DateRange[]): Date[] {
  return ranges.flatMap((range) =>
    getDatesBetween(fromDatabaseFormat(range.from), fromDatabaseFormat(range.to)),
  );
}

export function isDateInRange(date: Date, range: DateRange): boolean {
  return isWithinInterval(date, {
    start: fromDatabaseFormat(range.from),
    end: endOfDay(fromDatabaseFormat(range.to)),
  });
}

export function isDateInRanges(date: Date, ranges: DateRange[]): boolean {
  return ranges.some((range) => isDateInRange(date, range));
}

export function getDaySequence(from: string, to: string) {
  const fromDate = fromDatabaseFormat(from);
  const toDate = fromDatabaseFormat(to);

  return getDatesBetween(fromDate, toDate).map((date) => toDatabaseFormat(date));
}

const DEFAULT_DATE_FORMATS = {
  DAY: "d",
  MONTH: "MMM",
  YEAR: "yyyy",
  SEPARATOR: "-",
};

const defaultFormatOptions: FormatOptions = {
  locale: fr,
};

type FormatDateOptions = {
  dayFormat?: string;
  monthFormat?: string;
  yearFormat?: string;
  separator?: string;
  formatOptions?: FormatOptions;
};

export const formatDateRange = (
  from: Date | string,
  to: Date | string,
  options?: FormatDateOptions,
) => {
  const {
    dayFormat = DEFAULT_DATE_FORMATS.DAY,
    monthFormat = DEFAULT_DATE_FORMATS.MONTH,
    yearFormat = DEFAULT_DATE_FORMATS.YEAR,
    separator = DEFAULT_DATE_FORMATS.SEPARATOR,
    formatOptions = defaultFormatOptions,
  } = options ?? {};
  const DATE_FORMAT = `${dayFormat} ${monthFormat} ${yearFormat}`;

  if (isSameDay(from, to)) return format(from, DATE_FORMAT, formatOptions);

  if (isSameMonth(from, to))
    return `${format(from, dayFormat, formatOptions)}${separator}${format(
      to,
      DATE_FORMAT,
      formatOptions,
    )}`;

  if (isSameYear(from, to)) {
    const dateFormatWithoutYear = `${dayFormat} ${monthFormat}`;
    return `${format(from, dateFormatWithoutYear, formatOptions)}${separator}${format(
      to,
      DATE_FORMAT,
      formatOptions,
    )}`;
  }

  return `${format(from, DATE_FORMAT, formatOptions)}${separator}${format(
    to,
    DATE_FORMAT,
    formatOptions,
  )}`;
};

export function getEventStartDate(dates?: EventDateType | null) {
  if (!dates || dates.schedules.length === 0) return new Date();
  let closingIndex = 0;
  const closingCount = dates.exceptionalClosingDates.length;
  for (const range of dates.schedules) {
    let current = fromDatabaseFormat(range.fromDate);
    const end = fromDatabaseFormat(range.toDate);
    while (!isAfter(current, end)) {
      while (
        closingIndex < closingCount &&
        isBefore(fromDatabaseFormat(dates.exceptionalClosingDates[closingIndex].date), current)
      ) {
        closingIndex++;
      }
      if (
        closingIndex >= closingCount ||
        !isSameDay(fromDatabaseFormat(dates.exceptionalClosingDates[closingIndex].date), current)
      ) {
        return current;
      }
      current = addDays(current, 1);
    }
  }
  return new Date();
}

export function getEventEndDate(dates?: EventDateType | null) {
  if (!dates || dates.schedules.length === 0) return new Date();
  let closingIndex = dates.exceptionalClosingDates.length - 1;
  for (let i = dates.schedules.length - 1; i >= 0; i--) {
    const range = dates.schedules[i];
    let current = fromDatabaseFormat(range.toDate);
    const start = fromDatabaseFormat(range.fromDate);
    while (!isBefore(current, start)) {
      while (
        closingIndex >= 0 &&
        isAfter(fromDatabaseFormat(dates.exceptionalClosingDates[closingIndex].date), current)
      ) {
        closingIndex--;
      }
      if (
        closingIndex < 0 ||
        !isSameDay(fromDatabaseFormat(dates.exceptionalClosingDates[closingIndex].date), current)
      ) {
        return current;
      }
      current = addDays(current, -1);
    }
  }
  return new Date();
}

export function formatEventDate(
  schedules: EventScheduleType[],
  exceptions: EventExceptionalClosingDateType[],
  options?: FormatDateOptions,
) {
  if (!schedules || schedules.length === 0) return "";
  const startEvent = getEventStartDate({
    schedules,
    exceptionalClosingDates: exceptions,
    isContinuous: false,
  });
  const endEvent = getEventEndDate({
    schedules,
    exceptionalClosingDates: exceptions,
    isContinuous: false,
  });

  return formatDateRange(startEvent, endEvent, options);
}

export function getScheduleTimetableByDate(schedule: EventScheduleType, date: Date | string) {
  if (isBefore(date, schedule.fromDate) || isAfter(date, schedule.toDate)) return null;
  if (typeof date === "string") {
    date = new Date(date);
  }
  return schedule.timetables.find((timetable) => timetable.day === days[date.getDay()]);
}

function getSchedulesClosingDate(schedule: EventScheduleType[]) {
  const maxSchedule = maxBy(schedule, "toDate");

  if (isNullish(maxSchedule)) return null;

  const timetable = getScheduleTimetableByDate(maxSchedule, maxSchedule.toDate);

  const closingDate = maxSchedule.toDate;
  const closingTimeString = timetable?.closingTime ?? "18:00";

  const [hours, minutes] = closingTimeString.split(":");
  return setMinutes(setHours(closingDate, Number(hours)), Number(minutes));
}

export function getEventClosingDate(eventDates: EventDateType) {
  const schedulesClosingDate = getSchedulesClosingDate(eventDates.schedules);
  if (isNullish(schedulesClosingDate)) return null;
  return schedulesClosingDate;
}

export function getLastValidtyDate(dates: string[]) {
  return maxBy(dates, (date) => new Date(date).getTime());
}

export function getLastScheduleDateHour(schedules: EventScheduleType[]) {
  if (schedules.length === 0) {
    return null;
  }

  let lastDate = schedules[0].toDate;
  let lastHour = getScheduleTimetableByDate(schedules[0], lastDate)?.closingTime;

  schedules.forEach((schedule) => {
    if (isAfter(schedule.toDate, lastDate)) {
      lastDate = schedule.toDate;
      lastHour = getScheduleTimetableByDate(schedule, lastDate)?.closingTime;
    }
  });

  return { date: lastDate, hour: lastHour };
}

export function formatDatesByMonthYear(...rowDates: (Date | string)[]) {
  const dates = rowDates.map((date) => new Date(date));
  const datesByMonth = groupBy(dates, (date) => format(date, "MMMM\u00A0yyyy", { locale: fr }));
  const formattedDatesMonth = Object.entries(datesByMonth).map(([monthYear, dates]) => {
    const datesDay = dates.map((date) => format(date, "d", { locale: fr }));
    return `${datesDay.join("\u2011")}\u00A0${monthYear}`;
  });
  return formattedDatesMonth.join(", ");
}

// Issue with overload not working so well
export const getGroupedDates = (dates: Date[]) => {
  if (dates.length === 0) return [];

  const sortedDates = dates.sort(compareAsc);
  const groupedDates = [];

  let currentDate = sortedDates[0];
  let currentGroup: Date[] = [currentDate];

  for (let i = 1; i < sortedDates.length; i++) {
    const nextDate = sortedDates[i];

    if (differenceInCalendarDays(nextDate, currentDate) === 1) {
      currentGroup.push(nextDate);
    } else {
      groupedDates.push(currentGroup);
      currentGroup = [nextDate];
    }

    currentDate = nextDate;
  }

  groupedDates.push(currentGroup);

  return groupedDates;
};

export const getGroupedDatesString = (dates: string[]) => {
  if (dates.length === 0) return [];

  const sortedDates = dates.sort(compareAsc);
  const groupedDates = [];

  let currentDate = sortedDates[0];
  let currentGroup: string[] = [currentDate];

  for (let i = 1; i < sortedDates.length; i++) {
    const nextDate = sortedDates[i];

    if (differenceInCalendarDays(nextDate, currentDate) === 1) {
      currentGroup.push(nextDate);
    } else {
      groupedDates.push(currentGroup);
      currentGroup = [nextDate];
    }

    currentDate = nextDate;
  }

  groupedDates.push(currentGroup);

  return groupedDates;
};

export const getGroupedDatesExtremities = (groupedDates: Date[][]) => {
  return groupedDates.map((dates) => ({
    from: dates[0],
    to: dates[dates.length - 1],
  }));
};

// This is not the same type
// eslint-disable-next-line sonarjs/no-identical-functions
export const getGroupedDatesStringExtremities = (groupedDates: string[][]) => {
  return groupedDates.map((dates) => ({
    from: dates[0],
    to: dates[dates.length - 1],
  }));
};

export const getDatesAsStringRanges = (dates: Date[]): DateRange[] => {
  return getGroupedDatesExtremities(getGroupedDates(dates)).map(({ from, to }) => ({
    from: toDatabaseFormat(from),
    to: toDatabaseFormat(to),
  }));
};

export function formatDatesAsRanges(dates: Date[]) {
  if (dates.length === 0) {
    return "Aucune date renseignée";
  }
  const groupedDatesExtremities = getGroupedDatesExtremities(getGroupedDates(dates));
  const labels = groupedDatesExtremities.map(({ from, to }) => formatDateRange(from, to));
  return labels.join(" ; ");
}

export function formatDateStringAsRanges(dates: string[]) {
  if (dates.length === 0) {
    return "Aucune date renseignée";
  }
  const groupedDatesExtremities = getGroupedDatesStringExtremities(getGroupedDatesString(dates));
  const labels = groupedDatesExtremities.map(({ from, to }) => formatDateRange(from, to));
  return labels.join(" ; ");
}

export function formatDateStringRanges(ranges: DateRange[]): string {
  return ranges.map((range) => formatDateRange(range.from, range.to)).join(" ; ");
}

export function applyTimeToDate(date: Date, time: Time) {
  const newDate = new Date(date);
  newDate.setHours(time.hour, time.minute, time.second, time.millisecond);
  return newDate;
}

export function getTimeObjectFromDate(date: Date) {
  return new Time(date.getHours(), date.getMinutes(), date.getSeconds(), date.getMilliseconds());
}

export function formatTimeDifference(sentAt: string | undefined) {
  if (!sentAt) return "";
  const now = new Date();
  const date = parseISO(sentAt);

  const diffDays = differenceInDays(now, date);
  if (diffDays > 0) return `${diffDays}j`;

  const diffHours = differenceInHours(now, date);
  if (diffHours > 0) return `${diffHours}h`;

  const diffMinutes = differenceInMinutes(now, date);
  return `${diffMinutes}m`;
}

export const extractTime = (date: Date): string => {
  return format(date, "HH:mm", { locale: fr });
};

export function formatTime(hour: number, minute: number): string {
  const date = new Date();
  date.setHours(hour, minute, 0, 0);
  return format(date, "HH:mm");
}

export function parseHoursAndMinutesFromString(date: string) {
  const [hour, minute] = date.split(":").map((number) => Number(number));
  return { hour, minute };
}

export const getTimeFromString = (time: string) => {
  const { hour, minute } = parseHoursAndMinutesFromString(time);
  return new Time(hour, minute);
};

export const toDatabaseFormat = (date: Date | string): string => {
  return format(date, DATABASE_DATE_FORMAT, { locale: fr });
};

export const dateToDay = (date: Date | string): Day => {
  const dateObj = typeof date === "string" ? new Date(date) : date;
  return days[dateObj.getDay()];
};

export const fromDatabaseFormat = (date: string): Date => {
  return parse(date, DATABASE_DATE_FORMAT, new Date(), { locale: fr });
};

export const parseDateString = (date: string): Date => {
  if (isDatabaseDateFormat(date)) {
    return fromDatabaseFormat(date);
  }
  return parseISO(date);
};

export const fromDatabaseTimeFormat = (time: string): Time => {
  const [hours, minutes] = time.split(":").map(Number);
  return new Time(hours, minutes);
};

export const toDatabaseTimeFormat = (time: Date | string | Time | TimeValue): string => {
  const [hours, minutes] =
    time instanceof Date
      ? [time.getHours(), time.getMinutes()]
      : typeof time === "string"
        ? time.split(":").map(Number)
        : [time.hour, time.minute];
  const hh = String(hours).padStart(2, "0");
  const mm = String(minutes).padStart(2, "0");
  return `${hh}:${mm}`;
};

export const isDatabaseDateFormat = (date: string): boolean => {
  return isValid(parse(date, DATABASE_DATE_FORMAT, new Date(), { locale: fr }));
};

export const isDatabaseHourFormat = (date: string): boolean => {
  return isValid(parse(date, TIME_FORMAT, new Date(), { locale: fr }));
};

export const getDateFromDayHour = (date: string, hour: string) => {
  if (isDatabaseDateFormat(date)) {
    if (isDatabaseHourFormat(hour)) return new Date(date + " " + hour);
    return new Date(date);
  }
  if (isDatabaseHourFormat(hour)) return new Date(hour);
  return new Date();
};

type MinimalTimetableWithEndHour = Pick<Timetable, "day" | "endHour">;

// @TODO timetable date refactor should not be localized
export const fromTimetableWithEndHour = (timetable: MinimalTimetableWithEndHour): Date => {
  const date = fromDatabaseFormat(timetable.day);
  const endHour = parse(timetable.endHour, "HH:mm", new Date());

  const withEndHour = setHours(date, endHour.getHours());

  return setMinutes(withEndHour, endHour.getMinutes());
};

export function formatEventTime(startHour: string, endHour: string): string {
  const formatHour = (hour: string) =>
    hour.replace(/^0/, "").replace(/:00$/, "h").replace(/:/, "h");
  const start = formatHour(startHour);
  const end = formatHour(endHour);
  return `${start}-${end}`;
}

export function hasMatchingDate(array1: string[], array2: string[]): boolean {
  const formattedArray1 = new Set(array1);

  for (const date of array2) {
    if (formattedArray1.has(date)) {
      return true;
    }
  }
  return false;
}

export function areDatesIntersecting(
  firstRangeStart: Date,
  firstRangeEnd: Date,
  secondRangeStart: Date,
  secondRangeEnd: Date,
): boolean {
  if (isBefore(firstRangeEnd, firstRangeStart) || isBefore(secondRangeEnd, secondRangeStart))
    return true;
  return isBefore(firstRangeStart, secondRangeEnd) && isBefore(secondRangeStart, firstRangeEnd);
}

export function isDayBefore(date: Date, dateToCheck: Date): boolean {
  return differenceInCalendarDays(date, dateToCheck) < 0;
}

export function isDayAfter(date: Date, dateToCheck: Date): boolean {
  return differenceInCalendarDays(date, dateToCheck) > 0;
}

export function roundMinutesToInterval(date: Date, intervalMinutes: number): Date {
  const minutes = date.getMinutes();
  const roundedMinutes = Math.ceil(minutes / intervalMinutes) * intervalMinutes;
  return setMinutes(setHours(date, date.getHours()), roundedMinutes);
}

export function getCurrentTimeRoundedTo5Minutes(): string {
  const now = new Date();
  const rounded = roundMinutesToInterval(now, 5);
  return toDatabaseTimeFormat(rounded);
}

export function getCurrentTimeRoundedTo5MinutesPlusHour(): string {
  const now = new Date();
  now.setHours(now.getHours() + 1);
  const rounded = roundMinutesToInterval(now, 5);
  return toDatabaseTimeFormat(rounded);
}

export function getFirstActivityScheduleDate(schedules: SimpleActivity["schedules"]) {
  const day = minBy(schedules, (schedule) => schedule.timetable.day)?.timetable.day;
  if (isUndefined(day)) throw new Error("Error when getting first activity schedule date");
  return new Date(day);
}

export function getLastActivityScheduleDate(schedules: SimpleActivity["schedules"]) {
  const day = maxBy(schedules, (schedule) => schedule.timetable.day)?.timetable.day;
  if (isUndefined(day)) throw new Error("Error when getting last activity schedule date");
  return new Date(day);
}

export function getStartAndEndOfWeek(date: Date): {
  startOfWeek: Date;
  endOfWeek: Date;
} {
  const start = startOfWeek(date, { weekStartsOn: 1 });
  const end = endOfWeek(date, { weekStartsOn: 1 });
  const startAtMidnight = set(start, {
    hours: 0,
    minutes: 0,
    seconds: 0,
    milliseconds: 0,
  });
  const endAtMidnight = set(end, {
    hours: 0,
    minutes: 0,
    seconds: 0,
    milliseconds: 0,
  });
  return { startOfWeek: startAtMidnight, endOfWeek: endAtMidnight };
}

export const parseMailingCampaignDate = (d: { date: string | null; hour: string | null }) => {
  const { date, hour } = d;

  if (!date) {
    return null;
  }
  return hour
    ? parse(date + " " + hour, DATE_TIME_FORMAT, new Date())
    : parse(date, DATABASE_DATE_FORMAT, new Date());
};

type DatabaseDateRange = {
  start: string;
  end: string;
};

export function hasDatabaseDateRangeIntersection(
  firstRange: DatabaseDateRange,
  secondRange: DatabaseDateRange,
): boolean {
  if (firstRange.start > firstRange.end || secondRange.start > secondRange.end) {
    throw new Error("Les dates de fin doivent être après les dates de début");
  }

  return firstRange.start <= secondRange.end && secondRange.start <= firstRange.end;
}

type TimetableForFormat = {
  day: Day;
  state?: EventTimetableState;
  openingTime?: string | null;
  closingTime?: string | null;
  breakStartTime?: string | null;
  breakEndTime?: string | null;
};

const formatTimeString = (timeString: string): string => {
  const [hour, minute] = timeString.split(":");
  return `${hour.padStart(2, "0")}:${minute.padStart(2, "0")}`;
};

export const formatTimetableHours = (timetable: TimetableForFormat): string => {
  if (timetable.state === EventTimetableState.closed) {
    return "Fermé";
  }

  const opening = timetable.openingTime ?? "00:00";
  const closing = timetable.closingTime ?? "00:00";

  if (
    timetable.state === EventTimetableState.opened &&
    timetable.breakStartTime &&
    timetable.breakEndTime
  ) {
    return `${formatTimeString(opening)} - ${formatTimeString(timetable.breakStartTime)} et ${formatTimeString(timetable.breakEndTime)} - ${formatTimeString(closing)}`;
  }

  return `${formatTimeString(opening)} - ${formatTimeString(closing)}`;
};

export type FormattedScheduleDay = {
  day: Date;
  hours: string;
};

export const formatEventSchedulesByDay = (
  schedules: EventScheduleType[],
): FormattedScheduleDay[] => {
  const result: FormattedScheduleDay[] = [];

  schedules.forEach((schedule) => {
    const startDate = new Date(schedule.fromDate);
    const endDate = new Date(schedule.toDate);

    let currentDate = new Date(startDate);
    while (!isAfter(currentDate, endDate)) {
      const dayOfWeek = days[currentDate.getDay()];
      const timetable = schedule.timetables.find((t) => t.day === dayOfWeek);

      if (timetable) {
        const hours = formatTimetableHours(timetable);
        result.push({
          day: new Date(currentDate),
          hours,
        });
      }

      currentDate = addDays(currentDate, 1);
    }
  });

  return result.sort((a, b) => a.day.getTime() - b.day.getTime());
};

export const formatCooldown = (ms: number): string => {
  const minutes = Math.floor(ms / 60000);
  const seconds = Math.floor((ms % 60000) / 1000);
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
};

export const isPackDowngradeBlockedForEvent = (eventStartDate: Date): boolean => {
  const nowInParis = toZonedTime(new Date(), "Europe/Paris");
  const dayBeforeEvent = subDays(eventStartDate, 1);
  const blockStartTime = set(dayBeforeEvent, {
    hours: 23,
    minutes: 0,
    seconds: 0,
    milliseconds: 0,
  });
  return !isBefore(nowInParis, blockStartTime);
};
